import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../themes";
import {
  Card,
  CardHeader,
  CardBody,
  Avatar,
  Button,
  Textarea,
  Spinner,
  Divider,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@heroui/react";
import {
  HeartIcon,
  ChatBubbleLeftIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import MuxPlayer from "@mux/mux-player-react";
import "./VideoPage.css";
import CenteredHeroNav from "../components/CenteredHeroNav";
import { likeVideo, unlikeVideo, getVideoLikeState, likeComment, unlikeComment } from "../api/likes";
import { createComment, getComments } from "../api/comments";
import CommentThread from "./CommentThread";
import { useAuth } from "../contexts/AuthContext";
import LoginModal from "../components/LoginModal";
import SignUpModal from "../components/SignUpModal";
import CommentErrorModal from "../components/CommentErrorModal";
import { RateLimitPresets } from "../utils/rateLimiter";
import { generateVideoSlug, parseSlugToVideoId, updateVideoSEO } from "../utils/seoHelpers";

const VideoPage = () => {
  const { videoId, slug } = useParams(); // Support both :videoId and :slug
  const navigate = useNavigate();
  const { currentUser, userProfile, getIdToken } = useAuth();
  const { currentMode } = useTheme();
  const isDark = currentMode === 'dark';
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [commentErrorModalOpen, setCommentErrorModalOpen] = useState(false);
  const [commentErrorType, setCommentErrorType] = useState('generic');
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(null); // ✅ FIX: Track video player errors
  const [errorType, setErrorType] = useState(null); // 'not-found', 'removed', 'player-error'
  const videoRef = useRef();
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Like state
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likingInProgress, setLikingInProgress] = useState(false);
  const [commentLikingInProgress, setCommentLikingInProgress] = useState(new Set());
  const likeDebounceTimerRef = useRef(null); // ✅ FIX: Debounce timer for like button

  // ✅ FIX: Rate limiters for like operations
  const videoLikeRateLimiter = useRef(RateLimitPresets.likes());
  const commentLikeRateLimiter = useRef(RateLimitPresets.likes());

  // Login prompt modal
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Track if view has been counted
  const [viewCounted, setViewCounted] = useState(false);

  useEffect(() => {
    const fetchVideo = async () => {
      setLoading(true);
      try {
        const baseUrl =
          process.env.REACT_APP_API_BASE || "http://localhost:8080";

        // Determine actual video ID (from slug or direct ID)
        const actualVideoId = slug ? parseSlugToVideoId(slug) : videoId;

        // ✅ FIX: Use individual video endpoint instead of fetching all videos
        const res = await fetch(`${baseUrl}/api/videos/${actualVideoId}`);
        if (!res.ok) {
          if (res.status === 404) {
            setErrorType('not-found');
            throw new Error("Video not found");
          }
          if (res.status === 410) {
            setErrorType('removed');
            throw new Error("Video removed");
          }
          setErrorType('not-found');
          throw new Error("Failed to fetch video");
        }

        const foundVideo = await res.json();

        // Check if video is removed or restricted
        if (foundVideo.status === 'removed' || foundVideo.status === 'restricted') {
          setErrorType('removed');
          setVideo(null);
        } else {
          setVideo(foundVideo);
          // Initialize like count from video data
          setLikeCount(foundVideo.likeCount || 0);

          // Generate proper slug for this video
          const properSlug = generateVideoSlug(
            foundVideo.title,
            foundVideo.location || foundVideo.state,
            foundVideo.id
          );

          // SEO: Update meta tags and structured data
          updateVideoSEO(foundVideo, properSlug);

          // 301 Redirect: If user accessed via old URL, redirect to slug URL
          if (videoId && !slug) {
            // Legacy URL detected (/video/:id or /v/:id)
            navigate(`/clips/${properSlug}`, { replace: true });
          }
        }
      } catch (e) {
        console.error('[VideoPage] Failed to fetch video:', e);
        setVideo(null);
        if (!errorType) {
          setErrorType('not-found');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, slug]);

  // ✅ FIX: Handle video player errors
  const handleVideoError = (error) => {
    console.error("Video player error:", error);
    setErrorType('player-error');
    setVideoError({
      message: "We're having trouble playing this video.",
      details: error?.message || "Unknown error",
    });
  };

  // Increment view count when video starts playing
  const handleVideoPlay = async () => {
    if (viewCounted || !video?.id) return;

    setViewCounted(true);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE || "http://localhost:8080";
      const response = await fetch(`${baseUrl}/api/videos/${video.id}/view`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        // Update video state with new view count
        setVideo(prev => ({ ...prev, views: data.views }));
      }
    } catch (error) {
      console.error('[VideoPage] Failed to increment view count:', error);
    }
  };

  // Fetch like state when video or user changes
  useEffect(() => {
    const fetchLikeState = async () => {
      if (!video?.id) return;

      try {
        const result = await getVideoLikeState(video.id, currentUser ? getIdToken : null);
        if (result.success && result.data) {
          setLiked(result.data.liked);
          setLikeCount(result.data.likeCount);
        }
      } catch (error) {
        console.error('[VideoPage] Failed to fetch like state:', error);
      }
    };

    if (video?.id) {
      fetchLikeState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.id, currentUser]);

  // Fetch comments (like states now included in backend response)
  useEffect(() => {
    const fetchComments = async () => {
      if (!video?.id) return;

      setLoadingComments(true);
      try {
        const result = await getComments(video.id, getIdToken);
        if (result.success && result.data) {
          setComments(result.data);
        }
      } catch (error) {
        console.error('[VideoPage] Failed to fetch comments:', error);
      } finally {
        setLoadingComments(false);
      }
    };

    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.id]);

  // ✅ FIX: Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (likeDebounceTimerRef.current) {
        clearTimeout(likeDebounceTimerRef.current);
      }
    };
  }, []);

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return;

    // Check if user is logged in and profile is loaded
    if (!currentUser) {
      onOpen(); // Show login modal
      return;
    }

    // CRITICAL: Wait for userProfile to load to avoid username/displayName mismatch
    // userProfile comes from MongoDB (source of truth), currentUser comes from Firebase
    if (!userProfile) {
      console.warn("User profile not loaded yet, please wait...");
      return;
    }

    setSubmitting(true);

    // ✅ FIX: Use ONLY MongoDB userProfile data (never Firebase currentUser)
    // to match backend which uses MongoDB profile
    // IMPORTANT: Backend returns snake_case (photo_url, display_name), not camelCase
    const optimisticComment = {
      id: `temp-${Date.now()}`, // Temporary ID
      videoId: video.id,
      userId: currentUser.uid,
      username: userProfile.display_name || userProfile.username || "Anonymous",
      userAvatar: userProfile.photo_url, // Backend returns full CDN URL
      content: newComment,
      likeCount: 0,
      replyCount: 0,
      liked: false,
      createdAt: new Date().toISOString(),
      isPending: true, // Mark as pending
    };

    // ✅ FIX: Optimistic update - add comment immediately
    setComments([optimisticComment, ...comments]);
    setNewComment("");

    try {
      const result = await createComment(video.id, newComment, getIdToken);
      if (result.success && result.data) {
        // ✅ FIX: Replace optimistic comment with real comment from server
        setComments((prevComments) =>
          prevComments.map((c) =>
            c.id === optimisticComment.id ? { ...result.data, isPending: false } : c
          )
        );
      } else {
        // ✅ FIX: Remove optimistic comment on error
        setComments((prevComments) =>
          prevComments.filter((c) => c.id !== optimisticComment.id)
        );

        // Check if error is EMAIL_NOT_VERIFIED
        if (result.error?.code === "EMAIL_NOT_VERIFIED" || result.error === "EMAIL_NOT_VERIFIED") {
          setCommentErrorType('EMAIL_NOT_VERIFIED');
          setCommentErrorModalOpen(true);
        } else {
          setCommentErrorType('generic');
          setCommentErrorModalOpen(true);
        }
      }
    } catch (error) {
      // ✅ FIX: Remove optimistic comment on exception
      setComments((prevComments) =>
        prevComments.filter((c) => c.id !== optimisticComment.id)
      );
      setCommentErrorType('generic');
      setCommentErrorModalOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle video like/unlike
  const handleVideoLike = async () => {
    // If not logged in, show login prompt
    if (!currentUser) {
      onOpen();
      return;
    }

    // ✅ FIX: Rate limit like operations
    if (!videoLikeRateLimiter.current.canProceed()) {
      const waitTime = videoLikeRateLimiter.current.getTimeUntilNextCall();
      alert(`Too many like requests. Please wait ${Math.ceil(waitTime / 1000)} seconds.`);
      return;
    }

    // ✅ FIX: Prevent multiple simultaneous requests or debounce in progress
    if (likingInProgress) return;

    // ✅ FIX: Clear existing debounce timer
    if (likeDebounceTimerRef.current) {
      clearTimeout(likeDebounceTimerRef.current);
    }

    // ✅ FIX: Debounce the like action by 300ms
    likeDebounceTimerRef.current = setTimeout(async () => {
      setLikingInProgress(true);

      // Optimistic update
      const previousLiked = liked;
      const previousCount = likeCount;
      setLiked(!liked);
      setLikeCount(liked ? likeCount - 1 : likeCount + 1);

      try {
        const result = liked
          ? await unlikeVideo(video.id, getIdToken)
          : await likeVideo(video.id, getIdToken);

        if (result.success && result.data) {
          // Update with server response
          setLiked(result.data.liked);
          setLikeCount(result.data.likeCount);
        } else {
          // Rollback on error
          setLiked(previousLiked);
          setLikeCount(previousCount);
      }
      } catch (error) {
        // Rollback on error
        setLiked(previousLiked);
        setLikeCount(previousCount);
      } finally {
        setLikingInProgress(false);
      }
    }, 300); // ✅ FIX: 300ms debounce delay
  };

  // Handle comment like/unlike
  const handleCommentLike = async (commentId) => {
    // If not logged in, show login prompt
    if (!currentUser) {
      onOpen();
      return;
    }

    // ✅ FIX: Rate limit comment like operations
    if (!commentLikeRateLimiter.current.canProceed()) {
      const waitTime = commentLikeRateLimiter.current.getTimeUntilNextCall();
      alert(`Too many like requests. Please wait ${Math.ceil(waitTime / 1000)} seconds.`);
      return;
    }

    // Prevent multiple simultaneous requests for the same comment
    if (commentLikingInProgress.has(commentId)) {
      return;
    }

    // Mark this comment as having a like operation in progress
    setCommentLikingInProgress((prev) => new Set(prev).add(commentId));

    // ✅ FIX: Move these declarations outside try block so they're accessible in catch
    // Find the comment to determine its current like state
    const findComment = (commentsList, id) => {
      for (const comment of commentsList) {
        if (comment.id === id) return comment;
      }
      return null;
    };

    const comment = findComment(comments, commentId);
    if (!comment) {
      return;
    }

    // Store previous state for rollback (must be outside try-catch for scope)
    const previousLiked = comment.liked;
    const previousCount = comment.likeCount || 0;

    try {

      // Optimistic update
      setComments((prevComments) =>
        prevComments.map((c) =>
          c.id === commentId
            ? {
                ...c,
                liked: !c.liked,
                likeCount: c.liked ? (c.likeCount || 0) - 1 : (c.likeCount || 0) + 1,
              }
            : c
        )
      );

      // Call API
      const result = comment.liked
        ? await unlikeComment(commentId, getIdToken)
        : await likeComment(commentId, getIdToken);

      if (result.success && result.data) {
        // Update with server response
        setComments((prevComments) =>
          prevComments.map((c) =>
            c.id === commentId
              ? { ...c, liked: result.data.liked, likeCount: result.data.likeCount }
              : c
          )
        );
      } else {
        // Rollback on error
        setComments((prevComments) =>
          prevComments.map((c) =>
            c.id === commentId
              ? { ...c, liked: previousLiked, likeCount: previousCount }
              : c
          )
        );
      }
    } catch (error) {
      // ✅ FIX: Rollback to previous state, not toggle current state
      // Bug was: using !c.liked toggles the CURRENT (already toggled) state
      // Should use: previousLiked to restore the ORIGINAL state
      setComments((prevComments) =>
        prevComments.map((c) =>
          c.id === commentId
            ? { ...c, liked: previousLiked, likeCount: previousCount }
            : c
        )
      );
    } finally {
      // Remove from in-progress set
      setCommentLikingInProgress((prev) => {
        const newSet = new Set(prev);
        newSet.delete(commentId);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col theme-gradient-surface">
        <CenteredHeroNav />
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" color="primary" label="Loading video..." />
        </div>
      </div>
    );
  }

  if (!video) {
    // For error states, just navigate to home page
    // We can't reliably extract location from slug, and video data isn't available
    const targetPath = '/';

    return (
      <div className="min-h-screen flex flex-col theme-gradient-surface">
        <CenteredHeroNav />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full bg-content1/80 backdrop-blur-xl border border-divider/50 shadow-2xl">
            <CardBody className="text-center py-10 px-6">
              {errorType === 'removed' ? (
                // Video removed/restricted state
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-warning-50/80 dark:bg-warning-500/20 flex items-center justify-center border-2 border-warning-200 dark:border-warning-500/30">
                    <span className="text-5xl">🚫</span>
                  </div>
                  <h2 className="text-2xl font-bold mb-3 bg-gradient-to-br from-warning-600 to-warning-400 dark:from-warning-400 dark:to-warning-300 bg-clip-text text-transparent">
                    This video is no longer available.
                  </h2>
                  <p className="text-default-600 dark:text-default-400 mb-6 text-base">
                    It may have been removed by the uploader or taken down.
                  </p>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold shadow-lg hover:scale-[1.02] transition-all"
                    onPress={() => navigate(targetPath)}
                  >
                    Go Home
                  </Button>
                </>
              ) : (
                // Generic not found state
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-danger-50/80 dark:bg-danger-500/20 flex items-center justify-center border-2 border-danger-200 dark:border-danger-500/30">
                    <span className="text-5xl">😕</span>
                  </div>
                  <h2 className="text-2xl font-bold mb-3 bg-gradient-to-br from-danger-600 to-danger-400 dark:from-danger-400 dark:to-danger-300 bg-clip-text text-transparent">
                    Uh oh — this clip couldn't be loaded.
                  </h2>
                  <div className="text-default-600 dark:text-default-400 mb-6 space-y-1 text-base">
                    <p>• The video may have been removed</p>
                    <p>• The uploader may have deleted it</p>
                    <p>• Or something just glitched</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      size="lg"
                      variant="bordered"
                      className="font-semibold border-2"
                      onPress={() => window.location.reload()}
                    >
                      Try Again
                    </Button>
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold shadow-lg hover:scale-[1.02] transition-all"
                      onPress={() => navigate(targetPath)}
                    >
                      Go Home
                    </Button>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col theme-gradient-surface">
      <CenteredHeroNav />

      {/* Main Content */}
      <div className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Back Button */}
          <Button
            variant="light"
            startContent={<ArrowLeftIcon className="w-5 h-5" />}
            onPress={() => {
              const state = video.state || 'Unknown';
              navigate(`/state/${encodeURIComponent(state)}`);
            }}
            className="text-default-900 hover:text-primary font-medium"
          >
            Back to {video.state || 'Videos'}
          </Button>

          {/* Video Player Card */}
          <Card className="bg-default-50/50 backdrop-blur-md shadow-xl w-full">
            <CardBody className="p-0">
              <div className="video-player-wrapper">
                {videoError ? (
                  // Player error state - temporary streaming issue
                  <div className="flex flex-col items-center justify-center h-96 bg-gradient-to-br from-danger-50/50 to-warning-50/50 dark:from-danger-900/20 dark:to-warning-900/20 rounded-lg border border-danger-200 dark:border-danger-500/30 p-8">
                    <div className="w-16 h-16 mb-6 rounded-full bg-danger-50/80 dark:bg-danger-500/20 flex items-center justify-center border-2 border-danger-200 dark:border-danger-500/30 animate-pulse">
                      <span className="text-4xl">⚠️</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 bg-gradient-to-br from-danger-600 to-warning-600 dark:from-danger-400 dark:to-warning-400 bg-clip-text text-transparent">
                      {videoError.message}
                    </h3>
                    <p className="text-default-600 dark:text-default-400 mb-1">Retrying...</p>
                    <p className="text-xs text-default-400 mb-6">{videoError.details}</p>
                    <div className="flex gap-3">
                      <Button
                        size="lg"
                        variant="bordered"
                        className="font-semibold border-2"
                        onPress={() => window.location.reload()}
                      >
                        Force Reload
                      </Button>
                      <Button
                        size="lg"
                        className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold shadow-lg hover:scale-[1.02] transition-all"
                        onPress={() => navigate(-1)}
                      >
                        Go Back
                      </Button>
                    </div>
                  </div>
                ) : (
                  <MuxPlayer
                    ref={videoRef}
                    playbackId={video.muxPlaybackId}
                    streamType="on-demand"
                    controls
                    poster={video.posterUrl}
                    className="video-player-responsive"
                    onPlay={handleVideoPlay}
                    onError={handleVideoError}
                  />
                )}
              </div>
            </CardBody>
          </Card>

          {/* Video Info Card */}
          <Card
            className="shadow-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(42, 157, 143, 0.15) 0%, rgba(233, 196, 106, 0.12) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1.5px solid rgba(42, 157, 143, 0.2)'
            }}
          >
            <CardBody className="gap-4 p-6">
              {/* Title */}
              <h1 className="text-3xl font-bold text-default-900 dark:text-white drop-shadow-sm">
                {video.title || "Untitled Video"}
              </h1>

              {/* Info Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Uploader Card */}
                {video.username && (
                  <div
                    className="rounded-lg p-2.5 border backdrop-blur-md"
                    style={{
                      background: 'rgba(255, 255, 255, 0.5)',
                      borderColor: 'rgba(42, 157, 143, 0.3)'
                    }}
                  >
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <Avatar
                        src={video.userAvatar || null}
                        name={video.username}
                        size="sm"
                        className="flex-shrink-0"
                      />
                      <div className="text-xs text-center truncate font-semibold text-default-900 dark:text-white w-full px-1">{video.username}</div>
                    </div>
                  </div>
                )}

                {/* Views Card */}
                <div
                  className="rounded-lg p-3 text-center border backdrop-blur-md"
                  style={{
                    background: isDark ? 'transparent' : 'rgba(255, 255, 255, 0.5)',
                    borderColor: 'rgba(42, 157, 143, 0.3)'
                  }}
                >
                  <div className="text-2xl font-bold text-default-900 dark:text-white">{video.views || 0}</div>
                  <div className="text-xs" style={{ color: isDark ? 'white' : 'rgba(42, 157, 143, 0.7)' }}>Views</div>
                </div>

                {/* Like Button Card */}
                <div
                  className="rounded-lg p-3 flex flex-col items-center justify-center gap-1 border backdrop-blur-md cursor-pointer hover:scale-105 transition-transform"
                  style={{
                    background: isDark ? 'transparent' : (liked ? 'rgba(244, 63, 94, 0.1)' : 'rgba(255, 255, 255, 0.5)'),
                    borderColor: liked ? 'rgba(244, 63, 94, 0.3)' : 'rgba(42, 157, 143, 0.3)'
                  }}
                  onClick={handleVideoLike}
                >
                  {likingInProgress ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      {liked ? (
                        <HeartIconSolid className="w-8 h-8 text-danger" />
                      ) : (
                        <HeartIcon className="w-8 h-8 text-default-400" />
                      )}
                      <div className="text-xs font-semibold" style={{ color: isDark ? 'white' : (liked ? 'rgb(244, 63, 94)' : 'rgba(42, 157, 143, 0.7)') }}>
                        {likeCount || 0}
                      </div>
                    </>
                  )}
                </div>

                {/* Location Card */}
                <div
                  className="rounded-lg p-3 flex flex-col items-center justify-center gap-1 border backdrop-blur-md"
                  style={{
                    background: isDark ? 'transparent' : 'rgba(255, 255, 255, 0.5)',
                    borderColor: 'rgba(42, 157, 143, 0.3)'
                  }}
                >
                  <div className="text-2xl">📍</div>
                  <div className="text-sm font-bold text-default-900 dark:text-white text-center">{video.location || video.state || "Unknown"}</div>
                </div>
              </div>

              {/* Description */}
              {video.description && (
                <div
                  className="rounded-lg p-3 border backdrop-blur-sm"
                  style={{
                    background: isDark ? 'transparent' : 'rgba(255, 255, 255, 0.3)',
                    borderColor: 'rgba(42, 157, 143, 0.2)'
                  }}
                >
                  <p className="text-default-700" style={{ color: isDark ? 'white' : undefined }}>
                    {video.description}
                  </p>
                </div>
              )}

              {/* Date Chip */}
              {video.createdAt && (
                <div className="pt-2">
                  <Chip
                    variant="flat"
                    size="sm"
                    style={{
                      background: 'rgba(42, 157, 143, 0.1)',
                      borderColor: 'rgba(42, 157, 143, 0.2)',
                      color: 'rgb(42, 157, 143)'
                    }}
                  >
                    📅 {new Date(video.createdAt * 1000).toLocaleDateString()}
                  </Chip>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Comments Section */}
          <Card className="bg-default-50/50 backdrop-blur-md shadow-lg">
            <CardHeader className="flex flex-col items-start gap-2 pb-4">
              <div className="flex items-center gap-2">
                <ChatBubbleLeftIcon className="w-6 h-6" />
                <h2 className="text-xl font-semibold">
                  Comments ({comments.reduce((total, comment) => total + 1 + (comment.replyCount || 0), 0)})
                </h2>
              </div>
            </CardHeader>
            <Divider />
            <CardBody className="gap-6 pt-6">
              {/* Comment Input - Logged In */}
              {currentUser ? (
                <div className="flex gap-3">
                  <Avatar
                    src={userProfile?.photo_url}
                    name={userProfile?.display_name || userProfile?.username || currentUser?.username}
                    size="md"
                  />
                  <div className="flex-1 space-y-3">
                    <Textarea
                      placeholder="Add a comment..."
                      value={newComment}
                      onValueChange={setNewComment}
                      minRows={3}
                      maxRows={8}
                      variant="bordered"
                      maxLength={300}
                      classNames={{
                        input: "resize-y",
                      }}
                      description={`${newComment.length}/300 characters`}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="flat"
                        onPress={() => setNewComment("")}
                        isDisabled={!newComment.trim()}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        color="primary"
                        onPress={handleCommentSubmit}
                        isLoading={submitting}
                        isDisabled={!newComment.trim() || newComment.length > 300}
                      >
                        Comment
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Card className="bg-default-100/50 shadow-none border-none">
                  <CardBody className="text-center py-6">
                    <ChatBubbleLeftIcon className="w-12 h-12 mx-auto mb-3 text-default-400" />
                    <h3 className="text-lg font-semibold mb-2">
                      Join the conversation
                    </h3>
                    <p className="text-default-500 mb-4">
                      Sign in to share your thoughts and engage with the community
                    </p>
                    <Button color="primary" variant="shadow" onPress={() => setIsLoginOpen(true)}>
                      Sign In to Comment
                    </Button>
                  </CardBody>
                </Card>
              )}

              {/* Comments List */}
              <div className={`space-y-4 relative ${comments.reduce((total, comment) => total + 1 + (comment.replyCount || 0), 0) > 12 ? 'max-h-[800px] overflow-y-auto pr-2' : ''}`}>
                {loadingComments ? (
                  <div className="text-center py-8">
                    <Spinner size="sm" />
                    <p className="text-default-400 mt-2">Loading comments...</p>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8">
                    <ChatBubbleLeftIcon className="w-12 h-12 mx-auto mb-2 text-default-400" />
                    <p className="text-default-700 font-medium">No comments yet. Be the first to comment!</p>
                  </div>
                ) : (
                  comments.map((comment, index) => (
                    <CommentThread
                      key={comment.id}
                      comment={comment}
                      videoId={video.id}
                      videoUploaderId={video.userId}
                      onLoginRequired={onOpen}
                      handleCommentLike={handleCommentLike}
                      index={index}
                      totalComments={comments.length}
                    />
                  ))
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Login Prompt Modal */}
      <Modal isOpen={isOpen} onClose={onClose} placement="center">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Sign in to like this
          </ModalHeader>
          <ModalBody>
            <p>
              Create an account to save likes and get better recommendations.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              Maybe later
            </Button>
            <Button color="primary" onPress={() => { onClose(); setIsLoginOpen(true); }}>
              Sign in / Create account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Auth Modals */}
      <LoginModal
        isOpen={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
        onSwitchToSignUp={() => { setIsLoginOpen(false); setIsSignUpOpen(true); }}
      />
      <SignUpModal
        isOpen={isSignUpOpen}
        onClose={() => setIsSignUpOpen(false)}
        onSwitchToLogin={() => { setIsSignUpOpen(false); setIsLoginOpen(true); }}
      />

      {/* Comment Error Modal */}
      <CommentErrorModal
        isOpen={commentErrorModalOpen}
        onClose={() => setCommentErrorModalOpen(false)}
        errorType={commentErrorType}
      />
    </div>
  );
};

export default VideoPage;
